/** @format */

import { FunctionComponent as FC, ReactNode, useContext, useRef } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { RichText } from './RichText';
import { Document } from '@contentful/rich-text-types';
import { Container, ContainerWrapper } from './Layout';
import { FindColor } from '../../utils/findThemeColor';
import { useInView } from 'framer-motion';
import { breakpointFrom, breakpointUpTo } from '../../theme/mixins';
import { AnimatedTitle } from './animatedTitle';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { P } from './Typography';

export const TextAnimation: FC<Props> = ({ label, backgroundColor, textColor, textSecondColor, type }) => {
  const theme = useContext(ThemeContext);
  const banner = label.content.some(el => el.nodeType === 'hr');
  const pageManifest = type === 'manifest';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const isOnlyTitle = type === 'animations-only-title';

  return (
    <Wrapper
      className="test"
      ref={ref}
      as="section"
      $isOnlyTitle={isOnlyTitle}
      $color={FindColor(backgroundColor.replace(' ', '.').toLowerCase(), theme!.color)}
      $textColor={FindColor(textColor.replace(' ', '.').toLowerCase(), theme!.color)}
      $textSecondColor={textSecondColor ? FindColor(textSecondColor, theme!.color) : ''}
      $banner={banner}
      $pageManifest={pageManifest}
    >
      <StyledContainer
        $isOnlyTitle={isOnlyTitle}
        $isInView={isInView}
        $banner={banner}
        $pageManifest={pageManifest}
        $textSecondColor={textSecondColor}
        $backgroundColor={FindColor(backgroundColor.replace(' ', '.').toLowerCase(), theme!.color)}
      >
        {isOnlyTitle ? <RichText text={label} customOptions={AnimationsOptions} /> : <RichText text={label} />}
      </StyledContainer>
    </Wrapper>
  );
};

type Props = {
  label: Document;
  backgroundColor: string;
  textColor: string;
  textSecondColor: string;
  type?: string;
};

const StyledContainer = styled(Container)<{
  $banner: boolean;
  $pageManifest: boolean;
  $textSecondColor: string;
  $backgroundColor: string;
  $isInView: boolean;
  $isOnlyTitle: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  h1 {
    position: relative;
  }

  em {
    position: relative;
    &::before {
      z-index: 1;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      ${breakpointUpTo.md`
  left:  ${({ $isOnlyTitle }: { $isOnlyTitle: boolean }) => $isOnlyTitle && '-4rem'};
  `}
      width: ${({ $isOnlyTitle }) => ($isOnlyTitle ? '140%' : '100%')};
      height: 100%;
      transform-origin: left;
      animation: ${({ $isInView }) => $isInView && 'maskAnimation 900ms ease-out forwards var(--delay)'};
      background: ${({ $backgroundColor }) => $backgroundColor};
    }
    font-size: clamp(5.75rem, 3.5rem + 10vw, 12.5rem);
    color: ${({ theme, $textSecondColor, $pageManifest }) =>
      $pageManifest
        ? theme.color.teal.light
        : $textSecondColor
        ? FindColor($textSecondColor ?? 'white', theme?.color)
        : theme.color.teal.dark};
  }

  h2 {
    z-index: 4;
    font-family: ${({ theme }) => theme.font.title};
    font-size: ${({ theme }) => `clamp(${theme.font.size[2.8125]}, 2.2917rem + 2.3148vw, ${theme.font.size[4.375]})`};
    font-style: normal;
    font-weight: 400;
    line-height: clamp(3.1875rem, 2.625rem + 2.5vw, 4.875rem);
    letter-spacing: clamp(0.02813rem, -0.0062rem + 0.1528vw, 0.13125rem);
    color: ${({ color, theme }) => color ?? theme.color.white};
    padding-bottom: 2.5rem;

    em {
      font-family: ${({ theme }) => theme.font.accent};
      font-size: clamp(4.75rem, 2.5rem + 10vw, 12.5rem);
      color: ${({ theme, $textSecondColor, $pageManifest }) =>
        $pageManifest
          ? theme.color.teal.light
          : $textSecondColor
          ? FindColor($textSecondColor ?? 'white', theme?.color)
          : theme.color.teal.dark};
      line-height: clamp(3.1875rem, 2.5417rem + 2.8704vw, 5.125rem);
    }
    ${breakpointFrom.xl`
  line-height:  ${({ $isOnlyTitle }: { $isOnlyTitle: boolean }) => $isOnlyTitle && '12rem'};
  `};
    letter-spacing: clamp(0.03125rem, -0.0071rem + 0.1704vw, 0.14625rem);
    font-weight: ${({ $isOnlyTitle }) => ($isOnlyTitle ? 600 : 700)};
  }

  h3 {
    text-wrap: balance;
    font-style: normal;
    font-weight: 400;
    line-height: clamp(2rem, 1.4167rem + 2.5926vw, 3.75rem);
    letter-spacing: clamp(0.04688rem, 0.0344rem + 0.0556vw, 0.08438rem);
    z-index: 4;
    position: relative;
    font-family: ${({ theme }) => theme.font.title};
    font-size: clamp(1.5rem, 1.0833rem + 1.8519vw, 2.75rem);
    font-style: normal;
    color: ${({ color, theme }) => color ?? theme.color.white};
    padding-bottom: ${({ $isOnlyTitle }) => ($isOnlyTitle ? '0' : '2.5rem')};
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transform-origin: left;
      animation: ${({ $isInView }) =>
        $isInView && 'maskAnimationDescription 600ms ease-out forwards calc(600ms + var(--delay))'};
      background: ${({ $backgroundColor, $isOnlyTitle }: { $backgroundColor: string; $isOnlyTitle: boolean }) =>
        !$isOnlyTitle && $backgroundColor};
    }
    em {
      font-family: ${({ theme }) => theme.font.accent};
      font-size: clamp(5.75rem, 3.5rem + 10vw, 12.5rem);
      color: ${({ theme, $textSecondColor, $pageManifest }) =>
        $pageManifest
          ? theme.color.teal.light
          : $textSecondColor
          ? FindColor($textSecondColor ?? 'white', theme?.color)
          : theme.color.teal.dark};
      line-height: clamp(3.1875rem, 2.5417rem + 2.8704vw, 5.125rem);
      letter-spacing: clamp(0.03125rem, -0.0071rem + 0.1704vw, 0.14625rem);
      font-style: normal;
      font-weight: 700;
    }
  }
  @keyframes maskAnimation {
    0% {
      width: ${({ $isOnlyTitle }) => ($isOnlyTitle ? '120%' : '100%')};
    }
    100% {
      transform: ${({ $isOnlyTitle }) => ($isOnlyTitle ? 'translate(70rem, 0)' : 'translate(50rem, 0)')};
      width: 0%;
    }
  }
  @keyframes maskAnimationDescription {
    0% {
      width: 100%;
    }
    100% {
      transform: translate(0, 15rem);
      width: 100%;
    }
  }
`;

const Wrapper = styled(ContainerWrapper)<{
  $textSecondColor: string;
  $banner: boolean;
  $pageManifest: boolean;
  $isOnlyTitle: boolean;
}>`
  --delay: 100ms;
  &.test:not(:first-of-type) {
    --delay: 1s;
  }
  padding-inline: ${({ $isOnlyTitle }) =>
    $isOnlyTitle ? '1rem' : 'clamp(0rem, -8.1818rem + 36.3636vw, 50rem) !important'};
  padding-block: clamp(3.75rem, 2.3333rem + 6.2963vw, 8rem);
  text-align: center;
`;

const AnimationsOptions: Options = {
  renderMark: {
    [MARKS.ITALIC]: (text: ReactNode) => <em>{text}</em>,
  },
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => <AnimatedTitle text={documentToPlainTextString(node)} />,
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // to avoid rendering empty paragraphs
      if (Array.isArray(children) && children.length === 1 && children[0] === '') return;
      return <P>{children}</P>;
    },
  },
};
